import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { LoadOptions } from 'devextreme/data';
import { Observable, filter, map } from 'rxjs';

import { CoreModuleName } from '@app/core/models/core-constant.model';
import { EidosConfigService } from '@common/config/eidos-config.service';
import { IEidosModuleConfiguration } from '@common/config/environment.interface';
import { DynamicApiResponse } from '@common/models/dynamic-api-response.model';
import { CoreFormatService } from '@common/services/core-format.service';
import { EidosBaseApiService } from '@common/services/eidos-base-api.service';
import { EidosLogService } from '@common/services/eidos-log.service';
import { EidosSecurityService } from '@common/services/eidos-security.service';

import { IReservationModuleAPIConfiguration } from '@app/res-common/models/rescom.model';
import { IReservationCommissionPayoutType } from '../models/res-cached-data.models';
import { IResApiCheckCommissionPayoutBatchParams, IResApiCreateCommissionPayoutBatchParams, IResApiDeleteCommissionPayoutParams, IResApiDmcCommissionItem, IResApiDmcCommissionItemListRow, IResApiGenerateCommissionPayoutBatchSelfInvoiceParams, IResApiGenerateRemittanceParams, IResApiGetCommissionPayoutAgenciesParams, IResApiGetCommissionPayoutBatchesParams, IResApiGetCommissionPayoutBkgListParams, IResApiGetCommissionPayoutParams, IResApiGetRemittanceParams, IResApiGetRemittanceResponse, IResApiManageCommissionPayoutBatchParams, IResApiOpenCommissionPayoutBatchParams, IResApiSendCommissionPayoutBatchToBcParams, IResApiSendCommissionPayoutBatchToFinanceParams, IResApiSendCommissionPayoutEmailSpecs, IResApiUnlockCommissionPayoutBkgsParams, IResApiUpdateCommissionAmount, IResApiUploadCommissionPayoutBatchPaymentParams, ResBonusCommissionDetail, ResCommissionCompanies, ResCommissionPayout, ResCommissionPayoutAK, ResCommissionPayoutBatch, ResDmcCommission, ResDmcCommissionItem, ReservationCommission, IResApiDmcCommissionTypeChoiceRow, IResDmcCommissionItemsResult } from '../models/res-commission.models';
import { IReservationApiAgency, ReservationAgency } from '../models/res-owner.model';

@Injectable({
  providedIn: 'root'
})
export class ReservationCommissionApiService extends EidosBaseApiService {

  constructor(
    public configService: EidosConfigService,
    public eidosSecurityService: EidosSecurityService,
    public http: HttpClient,
    public eidosLogService: EidosLogService,
    public domSanitizer: DomSanitizer,
    public coreFormatService: CoreFormatService
  ) {
    super(coreFormatService, configService, eidosLogService, eidosSecurityService, http);
    this.configService.currentModulesConfig
      .pipe(
        filter(
          (modules: Array<IEidosModuleConfiguration>) => !!modules.find((module) => module.moduleName === CoreModuleName.Reservation)
        )
      )
      .subscribe((modules) => {
        const module = modules.find((module) => module.moduleName === CoreModuleName.Reservation);
        if (module) {
          const reservationModuleAPI = module.API as IReservationModuleAPIConfiguration;
          this.urlAPI = reservationModuleAPI.url;
          this.staticUrlAPI = reservationModuleAPI.staticUrl;
          this.pluginUrlAPI = reservationModuleAPI.pluginUrl;
          this.apiVersion = reservationModuleAPI.version;
          if (reservationModuleAPI.headers) {
            this.headers = {
              ...reservationModuleAPI.headers,
              ...this.headers
            };
          }
        }
      });
  }

  private fillPaginatedParams(params?: any, loadOptions?: LoadOptions) {
    if (!params) params = {};
    if (!loadOptions) return params;

    // Paginazione
    const skip = loadOptions.skip ?? 0;
    const take = loadOptions.take ?? 20;
    params.PageNum = Math.floor(skip / take) + 1;
    params.PageSize = take;

    // Filtri
    if (loadOptions.filter) {
      const filters = Array.isArray(loadOptions.filter[0])
        ? loadOptions.filter
        : [loadOptions.filter];

      filters.forEach((filter: any) => {
        if (Array.isArray(filter)) {
          const [field, _operator, value] = filter;
          params[field] = value;  // Nome più chiaro
        }
      });
    }

    // Ordinamento
    if (loadOptions.sort) {
      const sort = loadOptions.sort as { selector: string; desc: boolean }[];
      params.OrderField = sort[0]?.selector;
      params.OrderDirect = sort[0]?.desc ? 1 : 0;
    }

    return params;
  }

  checkCommissionPayoutBatch(params: IResApiCheckCommissionPayoutBatchParams): Observable<{ BkgID: number, CommissionPayoutDetID: number }[]> {
    return this.postDynamicAPI('commissionPayout/checkBatch', params).pipe(
      map<DynamicApiResponse, { BkgID: number, CommissionPayoutDetID: number }[]>((response) => response.body)
    )
  }

  checkCommissionPayoutBatchAK(params: IResApiCheckCommissionPayoutBatchParams): Observable<{ BkgID: number, CommissionPayoutDetID: number }[]> {
    return this.postDynamicAPI('commissionPayout/checkBatchAK', params).pipe(
      map<DynamicApiResponse, { BkgID: number, CommissionPayoutDetID: number }[]>((response) => response.body)
    )
  }

  completeCommissionPayoutBatch(params: { batchID: number }): Observable<any> {
    return this.postDynamicAPI('commissionPayout/completeBatch', params)
  }

  completeCommissionPayoutBatchAK(params: { batchID: number }): Observable<any> {
    return this.postDynamicAPI('commissionPayout/completeBatchAK', params)
  }

  createCommissionPayoutBatch(params: IResApiCreateCommissionPayoutBatchParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/createBatch', params);
  }

  createCommissionPayoutBatchAK(params: IResApiCreateCommissionPayoutBatchParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/createBatchAK', params);
  }

  deleteCommissionPayout(params: IResApiDeleteCommissionPayoutParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/delete', params);
  }

  deleteCommissionPayoutAK(params: IResApiDeleteCommissionPayoutParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/deleteAK', params);
  }

  generateCommissionPayoutBatchSelfInvoice(params: IResApiGenerateCommissionPayoutBatchSelfInvoiceParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/generateBatchSelfInvoice', params)
  }

  generateCommissionPayoutBatchSelfInvoiceAK(params: IResApiGenerateCommissionPayoutBatchSelfInvoiceParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/generateBatchSelfInvoiceAK', params)
  }

  public generateRemittance(params: IResApiGenerateRemittanceParams): Observable<any> {
    return this.postDynamicAPI('booking/generateRemittance', params);
  }

  getBonusCommissionDetail(bkgID?: number): Observable<ResBonusCommissionDetail[]> {
    return this.getDynamicAPI('commission/getBonusCommission25Details', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, ResBonusCommissionDetail[]>((response) => {
        return response.body.map((data: any) => new ResBonusCommissionDetail(data))
      })
    );
  }

  public getCommission(bkgID: number): Observable<any> {
    return this.callDynamicAPI('commission/get', { BkgID: bkgID, Detail: 'Y', Fe: 'Y' }).pipe(
      map<DynamicApiResponse, Array<any>>((response) => {
        return response.body.map((c: any) => new ReservationCommission(c));
      })
    );
  }

  getCommissionPayout(params: IResApiGetCommissionPayoutParams, loadOptions: any): Observable<{ data: ResCommissionPayout[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);

    return this.callDynamicAPI('commissionPayout/select', params, 'GET').pipe(
      map<DynamicApiResponse, { data: ResCommissionPayout[], totalCount: number }>((response) => {
        return {
          data: response.body.map((c: any) => new ResCommissionPayout(c)),
          totalCount: response.recordsCount
        }
      })
    );
  }

  getCommissionPayoutAK(params: IResApiGetCommissionPayoutParams, loadOptions: any): Observable<{ data: ResCommissionPayoutAK[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);

    return this.callDynamicAPI('commissionPayout/selectAK', params, 'GET').pipe(
      map<DynamicApiResponse, { data: ResCommissionPayoutAK[], totalCount: number }>((response) => {
        return {
          data: response.body.map((c: any) => new ResCommissionPayoutAK(c)),
          totalCount: response.recordsCount
        }
      })
    );
  }

  getCommissionPayoutBatchAgencies(params: IResApiGetCommissionPayoutAgenciesParams): Observable<ReservationAgency[]> {
    return this.callDynamicAPI('commissionPayout/selectBatchAgencies', params, 'GET').pipe(
      map<DynamicApiResponse, ReservationAgency[]>((response) =>
        response.body.map((c: IReservationApiAgency) => new ReservationAgency(c))
      )
    );
  }

  getCommissionPayoutBatchAgenciesAK(params: IResApiGetCommissionPayoutAgenciesParams): Observable<ReservationAgency[]> {
    return this.callDynamicAPI('commissionPayout/selectBatchAgenciesAK', params, 'GET').pipe(
      map<DynamicApiResponse, ReservationAgency[]>((response) =>
        response.body.map((c: IReservationApiAgency) => new ReservationAgency(c))
      )
    );
  }

  getCommissionPayoutBatches(params: IResApiGetCommissionPayoutBatchesParams, loadOptions: any): Observable<{ data: ResCommissionPayoutBatch[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);

    return this.callDynamicAPI('commissionPayout/selectBatches', params, 'GET').pipe(
      map<DynamicApiResponse, { data: ResCommissionPayoutBatch[], totalCount: number }>((response) => {
        return {
          data: response.body.map((c: any) => new ResCommissionPayoutBatch(c)),
          totalCount: response.recordsCount
        }
      })
    );
  }

  getCommissionPayoutBatchesAK(params: IResApiGetCommissionPayoutBatchesParams, loadOptions: any): Observable<{ data: ResCommissionPayoutBatch[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);

    return this.callDynamicAPI('commissionPayout/selectBatchesAK', params, 'GET').pipe(
      map<DynamicApiResponse, { data: ResCommissionPayoutBatch[], totalCount: number }>((response) => {
        return {
          data: response.body.map((c: any) => new ResCommissionPayoutBatch(c)),
          totalCount: response.recordsCount
        }
      })
    );
  }

  getCommissionPayoutBkgList(params: IResApiGetCommissionPayoutBkgListParams, loadOptions?: any): Observable<{ data: ResCommissionPayout[], totalCount: number } | ResCommissionPayout[]> {
    if (!loadOptions) {
      return this.postDynamicAPI('commissionPayout/selectBkgList', params).pipe(
        map<DynamicApiResponse, ResCommissionPayout[]>((response) =>
          response.body.map((c: any) => new ResCommissionPayout(c))
        )
      );
    }

    params = this.fillPaginatedParams(params, loadOptions);

    return this.postDynamicAPI('commissionPayout/selectBkgList', params).pipe(
      map<DynamicApiResponse, { data: ResCommissionPayout[], totalCount: number }>((response) => {
        return {
          data: response.body.map((c: any) => new ResCommissionPayout(c)),
          totalCount: response.recordsCount
        }
      })
    );
  }

  getCommissionPayoutBkgListAK(params: IResApiGetCommissionPayoutBkgListParams, loadOptions?: any): Observable<{ data: ResCommissionPayoutAK[], totalCount: number } | ResCommissionPayoutAK[]> {
    if (!loadOptions) {
      return this.postDynamicAPI('commissionPayout/selectBkgListAK', params).pipe(
        map<DynamicApiResponse, ResCommissionPayoutAK[]>((response) =>
          response.body.map((c: any) => new ResCommissionPayoutAK(c))
        )
      );
    }

    params = this.fillPaginatedParams(params, loadOptions);

    return this.postDynamicAPI('commissionPayout/selectBkgListAK', params).pipe(
      map<DynamicApiResponse, { data: ResCommissionPayoutAK[], totalCount: number }>((response) => {
        return {
          data: response.body.map((c: any) => new ResCommissionPayoutAK(c)),
          totalCount: response.recordsCount
        }
      })
    );
  }

  getCommissionPayoutCompanies(): Observable<ResCommissionCompanies[]> {
    return this.callDynamicAPI('commissionPayout/getCompany', 'GET').pipe(
      map<DynamicApiResponse, ResCommissionCompanies[]>((response) =>
        response.body.map((c: any) => new ResCommissionCompanies(c))
      )
    );
  }

  getCommissionTypesAK(): Observable<IReservationCommissionPayoutType[]> {
    return this.callDynamicAPI('commissionPayout/getCommissionTypesAK', {}, 'GET').pipe(
      map<DynamicApiResponse, IReservationCommissionPayoutType[]>((response) =>
        response.body
      )
    );
  }

  public getDmcCommission(bkgID: number): Observable<ResDmcCommission[]> {
    return this.callDynamicAPI('booking/getDMCCommission', { BkgID: bkgID }, 'GET').pipe(
      map<DynamicApiResponse, ResDmcCommission[]>((response) => {
        if (!Array.isArray(response.body)) {
          return [];
        }

        return response.body.map((row: any) => new ResDmcCommission(row));
      })
    );
  }

  public getDmcCommissionItems(bkgID: number): Observable<IResDmcCommissionItemsResult> {
    return this.callDynamicAPI('booking/getDMCCommissionItems', { BkgID: bkgID }, 'GET').pipe(
      map<DynamicApiResponse, IResDmcCommissionItemsResult>((response) => {
        const body = response.body;
        const mapItems = (rows: unknown): ResDmcCommissionItem[] =>
          Array.isArray(rows) ? rows.map((row: IResApiDmcCommissionItem) => new ResDmcCommissionItem(row)) : [];

        return {
          freePercentageItems: mapItems(body?.DT0),
          fixedItems: mapItems(body?.DT1),
        };
      })
    );
  }

  public getRemittance(params: IResApiGetRemittanceParams): Observable<IResApiGetRemittanceResponse | undefined> {
    return this.callDynamicAPI('booking/getRemittance', params, 'GET').pipe(
      map<DynamicApiResponse, IResApiGetRemittanceResponse | undefined>((response) =>
        Array.isArray(response.body) ? response.body?.[0] : response.body
      )
    );
  }

  manageCommissionPayoutBatch(params: IResApiManageCommissionPayoutBatchParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/manageBatch', params);
  }

  manageCommissionPayoutBatchAK(params: IResApiManageCommissionPayoutBatchParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/manageBatchAK', params);
  }

  openCommissionPayoutBatch(params: IResApiOpenCommissionPayoutBatchParams, loadOptions: any): Observable<{ data: ResCommissionPayout[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);

    return this.postDynamicAPI('commissionPayout/selectBatch', params).pipe(
      map<DynamicApiResponse, { data: ResCommissionPayout[], totalCount: number }>((response) => {
        return {
          data: response.body.Bookings.map((c: any) => new ResCommissionPayout(c, response.body.DisplayFlags)),
          totalCount: response.recordsCount
        }
      })
    );
  }

  openCommissionPayoutBatchAK(params: IResApiOpenCommissionPayoutBatchParams, loadOptions: any): Observable<{ data: ResCommissionPayoutAK[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);

    return this.postDynamicAPI('commissionPayout/selectBatchAK', params).pipe(
      map<DynamicApiResponse, { data: ResCommissionPayoutAK[], totalCount: number }>((response) => {
        return {
          data: response.body.DT0.map((c: any) => new ResCommissionPayoutAK(c, response.body.DT1[0])),
          totalCount: response.recordsCount
        }
      })
    );
  }

  restoreCommissionFromTam(bkgID: number): Observable<any> {
    return this.callDynamicAPI('booking/restoreCommissionFromTam', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  public saveDmcCommission(
    bkgID: number,
    dmcCommissionItemList: IResApiDmcCommissionItemListRow[],
    dmcCommissionTypeChoice: IResApiDmcCommissionTypeChoiceRow[]
  ): Observable<any> {
    return this.postDynamicAPI('booking/saveDMCCommission', {
      BkgID: bkgID,
      DMCCommissionItemList: dmcCommissionItemList,
      DMCCommissionTypeChoice: dmcCommissionTypeChoice,
    });
  }

  sendCommissionPayoutBatchToBc(params: IResApiSendCommissionPayoutBatchToBcParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/sendBatchToBc', params)
  }

  sendCommissionPayoutBatchToBcAK(params: IResApiSendCommissionPayoutBatchToBcParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/sendRemittance', params).pipe(
      map<DynamicApiResponse, IResApiSendCommissionPayoutEmailSpecs | undefined>((response) => response.body?.[0])
    )
  }

  sendCommissionPayoutBatchToFinance(params: IResApiSendCommissionPayoutBatchToFinanceParams): Observable<IResApiSendCommissionPayoutEmailSpecs | undefined> {
    return this.postDynamicAPI('commissionPayout/sendBatchToFinance', params).pipe(
      map<DynamicApiResponse, IResApiSendCommissionPayoutEmailSpecs | undefined>((response) => response.body?.[0])
    )
  }

  sendCommissionPayoutBatchToFinanceAK(params: IResApiSendCommissionPayoutBatchToFinanceParams): Observable<IResApiSendCommissionPayoutEmailSpecs | undefined> {
    return this.postDynamicAPI('commissionPayout/sendBatchToFinanceAK', params).pipe(
      map<DynamicApiResponse, IResApiSendCommissionPayoutEmailSpecs | undefined>((response) => response.body?.[0])
    )
  }

  unlockComissionPayoutBkgs(params: IResApiUnlockCommissionPayoutBkgsParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/unlockBkgs', params)
  }

  unlockComissionPayoutBkgsAK(params: IResApiUnlockCommissionPayoutBkgsParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/unlockBkgsAK', params)
  }

  public updateCommissionAmount(params: IResApiUpdateCommissionAmount) {
    return this.postDynamicAPI('commissionAmount/update', params);
  }

  uploadCommissionPayoutBatchPayment(params: IResApiUploadCommissionPayoutBatchPaymentParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/uploadBatchPayment', params)
  }

  uploadCommissionPayoutBatchPaymentAK(params: IResApiUploadCommissionPayoutBatchPaymentParams): Observable<any> {
    return this.postDynamicAPI('commissionPayout/uploadBatchPaymentAK', params)
  }
}
